// frontend/src/pages/admin/CVGeneration/WorkExperienceSection.js
import React, { useState } from 'react';
import {
  Briefcase, Plus, Edit2, Trash2, AlertTriangle, Clock
} from 'lucide-react';
import { staffAPI } from '../../../services/api';
import toast from '../../../utils/toast';
import ConfirmModal from '../../../components/UI/ConfirmModal';
import WorkExperienceFormModal from '../../../components/employees/WorkExperienceFormModal';
import { formatWorkExperiencePeriod, getDataFreshness } from '../../../utils/dateUtils';
import { getWorkExpEmploymentTypeLabel } from '../../../utils/workExperienceUtils';

/**
 * Work Experience Section for CV Data Entry
 */
const WorkExperienceSection = ({ staffId, records = [], onDataChange }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleOpenAdd = () => {
    setEditingRecord(null);
    setShowModal(true);
  };

  const handleOpenEdit = (record) => {
    setEditingRecord(record);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRecord(null);
  };

  const handleSubmit = async (payload) => {
    setIsSubmitting(true);
    try {
      if (editingRecord) {
        await staffAPI.updateWorkExperience(editingRecord.id, payload);
        toast.success('Work experience updated');
      } else {
        await staffAPI.createWorkExperience(payload);
        toast.success('Work experience added');
      }
      handleCloseModal();
      onDataChange();
    } catch (error) {
      const data = error.response?.data;
      const errorMessage = data?.detail || data?.error
        || (typeof data === 'string' ? data : null)
        || 'Failed to save work experience';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (recordId) => {
    try {
      await staffAPI.deleteWorkExperience(recordId);
      toast.success('Work experience deleted');
      setDeleteConfirm(null);
      onDataChange();
    } catch (error) {
      toast.error('Failed to delete work experience');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Work Experience</h3>
            <p className="text-xs sm:text-sm text-gray-500 truncate">{records.length} record{records.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={handleOpenAdd}
          className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 btn-sm flex-shrink-0"
          aria-label="Add Work Experience"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Add Work Experience</span>
        </button>
      </div>

      {records.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <Briefcase className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h4 className="text-lg font-semibold text-gray-900 mb-2">No work experience yet</h4>
          <p className="text-sm text-gray-500">Add prior roles from a CV or LinkedIn.</p>
          <button
            onClick={handleOpenAdd}
            className="btn btn-admin-primary mt-4"
          >
            <Plus className="w-4 h-4" />
            Add first work experience
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Designation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Last Modified</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {records.map((record) => {
                const freshness = getDataFreshness(record.work_exp_updated_at);
                return (
                  <tr key={record.id} className="table-row">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {record.work_exp_company_name || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {[getWorkExpEmploymentTypeLabel(record.work_exp_employment_type), record.work_exp_country]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {record.work_exp_designation || 'N/A'}
                      </div>
                      {record.work_exp_department && (
                        <div className="text-xs text-gray-500">{record.work_exp_department}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatWorkExperiencePeriod(record.work_exp_start_date, record.work_exp_end_date, record.work_exp_is_current)}
                      {record.work_exp_is_current && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Current
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className={`flex items-center gap-1 text-xs ${freshness.isStale ? 'text-amber-600' : 'text-gray-500'}`}>
                        <Clock className="w-3 h-3" />
                        {freshness.label}
                        {freshness.isStale && <AlertTriangle className="w-3 h-3 ml-1" />}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenEdit(record)}
                          className="icon-button-admin"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(record)}
                          className="icon-button-danger"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {records.length > 0 && (
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <button
            onClick={handleOpenAdd}
            className="btn btn-admin-outline btn-sm w-full"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Another Work Experience
          </button>
        </div>
      )}

      <WorkExperienceFormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        staffId={staffId}
        record={editingRecord}
        existingRecords={records}
        theme="admin"
        showVerification
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        isOpen={Boolean(deleteConfirm)}
        title="Delete Work Experience"
        message={
          deleteConfirm
            ? `Delete ${deleteConfirm.work_exp_designation || 'this role'} at ${deleteConfirm.work_exp_company_name}? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        theme="admin"
        onConfirm={() => handleDelete(deleteConfirm.id)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
};

export default WorkExperienceSection;
